/**
 * CityLanding — dynamic per-city landing page mounted at
 * `/food-trucks/:citySlug`. Replaces the hand-built Austin page with
 * a single template that renders for any row in `expansion_cities`
 * whose `public_status` is `live` or `coming_soon` and whose landing
 * URL isn't the homepage (e.g. Central PA falls back to `/`).
 *
 * Content:
 *   • Hero banner + city-flavored H1
 *   • Preloaded/claimed/active counts (only if any trucks preloaded)
 *   • Founding Pro slot counter (when the city has an active promo)
 *   • Preloaded truck list w/ "claim your truck" affordance
 *   • Two waitlist forms (owners + customers)
 *   • Per-city <title> / <meta description> injected into <head>
 *
 * Missing cities → 404 fallback UI.
 */
import React, { useState, useEffect } from "react";
import { BACKEND_BASE } from "../lib/backendBase";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Truck,
  MapPin,
  Heart,
  PaperPlaneTilt,
  CookingPot,
  CaretRight,
} from "@phosphor-icons/react";

const API = `${BACKEND_BASE}/api`;

function WaitlistForm({ role, citySlug, ctaLabel, hint }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim();
    if (!cleanEmail || !cleanEmail.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email: cleanEmail,
        name: name.trim() || null,
        role,
        city_slug: citySlug,
      });
      setSubmitted(true);
      toast.success(
        role === "owner"
          ? "You're on the early-access list — we'll be in touch."
          : "Thanks! We'll ping you when TruckTrackr is live in your city.",
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Couldn't add you. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 text-sm text-emerald-900"
        data-testid={`waitlist-success-${role}`}
      >
        🎉 You&rsquo;re in. Check your inbox in the coming weeks &mdash; or follow{" "}
        <a href="https://thetrucktrackr.com" className="font-semibold underline">
          thetrucktrackr.com
        </a>
        {" "}for launch updates.
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-2" data-testid={`waitlist-form-${role}`}>
      {role === "owner" && (
        <Input
          type="text"
          placeholder="Truck or business name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-11 bg-white/95"
          data-testid={`waitlist-name-${role}`}
        />
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          type="email"
          required
          placeholder="you@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-11 bg-white/95 flex-1"
          data-testid={`waitlist-email-${role}`}
        />
        <Button
          type="submit"
          disabled={submitting}
          className="h-11 gap-1.5 bg-[#ea580c] hover:bg-[#c2410c] text-white whitespace-nowrap"
          data-testid={`waitlist-submit-${role}`}
        >
          <PaperPlaneTilt weight="duotone" className="h-4 w-4" />
          {submitting ? "Saving…" : ctaLabel}
        </Button>
      </div>
      <p className="text-[11px] text-amber-900/70">{hint}</p>
    </form>
  );
}

/** Small hook that sets document.title + meta description + robots
 *  when the component is mounted, and restores the previous values on
 *  unmount so SPA navigation doesn't leak city-specific tags. */
function useCityHead({ title, description, indexable }) {
  useEffect(() => {
    if (!title) return undefined;
    const prevTitle = document.title;
    document.title = title;

    let desc = document.querySelector("meta[name='description']");
    const prevDescContent = desc ? desc.getAttribute("content") : null;
    let createdDesc = false;
    if (!desc && description) {
      desc = document.createElement("meta");
      desc.setAttribute("name", "description");
      document.head.appendChild(desc);
      createdDesc = true;
    }
    if (desc && description) desc.setAttribute("content", description);

    let robots = document.querySelector("meta[name='robots']");
    const prevRobotsContent = robots ? robots.getAttribute("content") : null;
    let createdRobots = false;
    if (!indexable) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
        createdRobots = true;
      }
      robots.setAttribute("content", "noindex, follow");
    }

    return () => {
      document.title = prevTitle;
      if (createdDesc) desc.remove();
      else if (desc && prevDescContent !== null) desc.setAttribute("content", prevDescContent);
      if (createdRobots) robots.remove();
      else if (robots && prevRobotsContent !== null) robots.setAttribute("content", prevRobotsContent);
    };
  }, [title, description, indexable]);
}

export default function CityLanding() {
  const { citySlug } = useParams();
  const [city, setCity] = useState(null);        // expansion_cities row
  const [stats, setStats] = useState({});        // preloaded stats
  const [trucks, setTrucks] = useState([]);      // preloaded truck list
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const cityRes = await axios.get(`${API}/expansion-cities/${citySlug}`);
        if (cancelled) return;
        setCity(cityRes.data);
        // Fetch trucks + stats in parallel — silent-fail so the page
        // still renders the hero + waitlist even if the preload
        // endpoint hiccups.
        const [s, t] = await Promise.allSettled([
          axios.get(`${API}/preloaded-trucks/stats?city=${citySlug}`),
          axios.get(`${API}/preloaded-trucks?city=${citySlug}&limit=25`),
        ]);
        if (cancelled) return;
        setStats(s.status === "fulfilled" ? s.value.data || {} : {});
        setTrucks(t.status === "fulfilled" && Array.isArray(t.value.data) ? t.value.data : []);
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [citySlug]);

  // Head tags — computed even before city loads so the initial title
  // isn't stuck on the previous page's tab title.
  const cityLabel = city
    ? `${city.name}${city.state ? `, ${city.state}` : ""}`
    : "";
  const isLive = city?.public_status === "live";
  const headTitle = city
    ? `Food Trucks in ${cityLabel} · TruckTrackr`
    : "TruckTrackr";
  const headDesc = city
    ? isLive
      ? `Find food trucks in ${cityLabel} — live locations, weekly schedules, and menus, all on one map.`
      : `TruckTrackr is coming to ${cityLabel}. Join the early-access list for food trucks or get notified when we launch for customers.`
    : "";
  // Coming-soon cities are `noindex, follow` (visitors can share the
  // URL, but Google shouldn't rank them as active landing pages).
  // Live cities are fully indexable.
  useCityHead({
    title: headTitle,
    description: headDesc,
    indexable: isLive,
  });

  if (loading) {
    return (
      <div
        className="min-h-screen bg-[#fdf5e6] flex items-center justify-center text-amber-900/80 text-sm"
        data-testid="city-landing-loading"
      >
        Loading…
      </div>
    );
  }

  if (notFound || !city) {
    return (
      <div
        className="min-h-screen bg-[#fdf5e6] flex flex-col items-center justify-center px-6 text-center text-amber-950"
        data-testid="city-landing-not-found"
      >
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#ea580c] mb-3 hover:opacity-80 transition-opacity"
          aria-label="Back to TruckTrackr home"
          data-testid="city-landing-notfound-home-link"
        >
          <Truck weight="duotone" className="h-5 w-5" />
          <span className="text-xs font-bold tracking-[0.25em] uppercase">TruckTrackr</span>
        </Link>
        <h1 className="text-3xl sm:text-4xl font-black mb-2">That city isn&rsquo;t on our map yet.</h1>
        <p className="text-sm text-amber-900/80 max-w-md">
          We haven&rsquo;t launched TruckTrackr in this market yet.{" "}
          <Link to="/" className="underline text-[#ea580c] font-semibold">
            Head home
          </Link>{" "}
          to see where we&rsquo;re live.
        </p>
      </div>
    );
  }

  const cityDisplay = cityLabel;
  const promoActive = stats.founding_promo_active;
  const promoTotal = stats.founding_slots_total || 0;
  const promoAvailable = stats.founding_slots_available || 0;
  const promoLabel = stats.founding_promo_label || city.launch_promo_label;

  // Banner tagline flips based on public status so live cities don't
  // sound like they're still teasing a launch.
  const bannerEyebrow = isLive
    ? `Now serving · ${(city.state || cityDisplay).toUpperCase()}`
    : `The next stop · ${(city.state || cityDisplay).toUpperCase()}`;
  const bannerHeadline = isLive
    ? `We're live in ${city.name}.`
    : `${city.name}, you're next.`;

  return (
    <div
      className="min-h-screen bg-[#fdf5e6] text-amber-950 relative overflow-hidden"
      data-testid={`city-landing-${citySlug}`}
    >
      {/* Grit texture overlay — radial dot pattern for warmth without a photo. */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #92400e 1px, transparent 0)",
          backgroundSize: "20px 20px",
        }}
        aria-hidden="true"
      />

      <main className="relative max-w-2xl mx-auto px-5 sm:px-8 py-12 sm:py-16 space-y-10">
        <header className="space-y-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#ea580c] hover:opacity-80 transition-opacity"
            aria-label="Back to TruckTrackr home"
            data-testid="city-landing-home-link"
          >
            <Truck weight="duotone" className="h-5 w-5" />
            <span className="text-xs font-bold tracking-[0.25em] uppercase">
              TruckTrackr
            </span>
          </Link>
          <div
            className="relative rounded-2xl bg-[#ea580c] text-white px-5 py-4 sm:px-6 sm:py-5 shadow-lg shadow-orange-900/20 border-2 border-orange-700"
            data-testid="city-banner"
          >
            <div className="text-[10px] font-bold tracking-[0.32em] uppercase text-orange-100/85 mb-1">
              {bannerEyebrow}
            </div>
            <div className="text-2xl sm:text-3xl font-black tracking-tight leading-tight">
              {bannerHeadline}
            </div>
          </div>
        </header>

        {stats.trucks_preloaded > 0 && (
          <section
            className="grid grid-cols-3 gap-2 sm:gap-3 -mt-2"
            data-testid="city-preload-counts"
          >
            {[
              { label: "Preloaded", value: stats.trucks_preloaded || 0 },
              { label: "Claimed", value: stats.trucks_claimed || 0 },
              { label: "Live", value: stats.trucks_active || 0 },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-xl bg-white/85 border border-amber-200 px-3 py-2 text-center"
              >
                <div className="text-2xl sm:text-3xl font-black tabular-nums text-[#ea580c] leading-none">
                  {s.value}
                </div>
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-amber-800/80 mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </section>
        )}

        <section className="space-y-5">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
            {isLive ? (
              <>Find {city.name}&rsquo;s food trucks before they sell&nbsp;out.</>
            ) : (
              <>Find {city.name}&rsquo;s food trucks before they sell&nbsp;out.</>
            )}
          </h1>
          <p className="text-base sm:text-lg leading-relaxed text-amber-950/85 max-w-xl">
            {isLive ? (
              <>
                Real-time pins, weekly schedules, and menus for food trucks in{" "}
                <strong>{cityDisplay}</strong>. No more hunting through stale
                Instagram stories.
              </>
            ) : (
              <>
                We&rsquo;re bringing the map locals already trust in Central PA down
                to <strong>{cityDisplay}</strong>. Real-time pins, weekly schedules,
                zero hunting through stale Instagram stories.
              </>
            )}
          </p>
        </section>

        {/* Waitlist / claim cards */}
        <section className="space-y-4">
          <article className="rounded-2xl bg-white/80 backdrop-blur-sm border-2 border-amber-300 p-5 sm:p-6 space-y-3 shadow-sm">
            <div className="flex items-center gap-2">
              <CookingPot weight="duotone" className="h-5 w-5 text-amber-700" />
              <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-amber-700">
                Truck owners · {isLive ? "Claim your profile" : "Pilot list"}
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold leading-snug">
              {isLive
                ? `Own a truck in ${city.name}?`
                : `Want first dibs in ${city.name}?`}
            </h2>
            <p className="text-sm text-amber-950/80 leading-relaxed">
              {promoTotal > 0 && promoActive ? (
                <>
                  <span
                    className="inline-flex items-center gap-1 font-bold text-amber-900 bg-amber-100 px-2 py-0.5 rounded-full text-[11px] tracking-wide uppercase mr-1.5"
                    data-testid="city-founding-counter"
                  >
                    {promoAvailable} of {promoTotal} spots left
                  </span>
                  We&rsquo;re hand-picking the first {promoTotal} trucks in {city.name}.
                  Permanent free Pro + a &ldquo;{promoLabel || `${city.name} Founding Pro`} #N&rdquo;
                  badge on your profile &mdash; locked in for life. Drop your email
                  and we&rsquo;ll reach out personally.
                </>
              ) : promoTotal > 0 ? (
                <>
                  <span
                    className="inline-flex items-center gap-1 font-bold text-emerald-900 bg-emerald-100 px-2 py-0.5 rounded-full text-[11px] tracking-wide uppercase mr-1.5"
                    data-testid="city-founding-closed"
                  >
                    All {promoTotal} founding spots claimed
                  </span>
                  Our {city.name} Founding Pros are locked in! Join the waitlist to be
                  first in line when we open the next round.
                </>
              ) : isLive ? (
                <>
                  Claim your free profile and start posting your schedule to hungry
                  locals in {cityDisplay}. Upgrade to Pro any time.
                </>
              ) : (
                <>
                  We&rsquo;re quietly building the map for {cityDisplay}. Drop your
                  email and we&rsquo;ll reach out when the pilot opens.
                </>
              )}
            </p>
            <WaitlistForm
              role="owner"
              citySlug={citySlug}
              ctaLabel={isLive ? "Get on the list" : "Get early access"}
              hint={
                isLive
                  ? "We'll follow up with claim instructions."
                  : "One-time email. Pilot is invite-only — we'll reply within a week."
              }
            />
            {isLive && (
              <div className="pt-1">
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#ea580c] hover:text-[#c2410c]"
                  data-testid="city-signup-link"
                >
                  Or sign up directly <CaretRight weight="bold" className="h-3.5 w-3.5" />
                </Link>
              </div>
            )}
          </article>

          <article className="rounded-2xl bg-white/60 backdrop-blur-sm border border-amber-200 p-5 sm:p-6 space-y-3 shadow-sm">
            <div className="flex items-center gap-2">
              <Heart weight="duotone" className="h-5 w-5 text-rose-500" />
              <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-amber-700">
                Hungry humans · {isLive ? "Explore the map" : "Launch notify"}
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold leading-snug">
              Just here to eat?
            </h2>
            <p className="text-sm text-amber-950/80 leading-relaxed">
              {isLive ? (
                <>
                  Open the live map and see which trucks are parked where in{" "}
                  {cityDisplay} right now.
                </>
              ) : (
                <>
                  We&rsquo;ll ping you when the map goes live in {city.name} and you
                  can finally tell which trucks are parked where, right now.
                </>
              )}
            </p>
            {isLive ? (
              <Link
                to="/map"
                className="inline-flex items-center justify-center gap-1.5 rounded-md bg-[#ea580c] hover:bg-[#c2410c] text-white h-11 px-4 text-sm font-semibold"
                data-testid="city-open-map"
              >
                <MapPin weight="duotone" className="h-4 w-4" />
                Open the map
              </Link>
            ) : (
              <WaitlistForm
                role="customer"
                citySlug={citySlug}
                ctaLabel="Notify me"
                hint={`No spam — one email when we launch in ${city.name}.`}
              />
            )}
          </article>
        </section>

        {/* Preloaded truck list — same structure as the old Austin page */}
        {trucks.length > 0 && (
          <section className="space-y-3" data-testid="city-preloaded-trucks">
            <div className="space-y-1">
              <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-amber-700">
                Trucks on deck
              </div>
              <h2 className="text-xl sm:text-2xl font-bold leading-snug">
                {stats.trucks_preloaded}{" "}
                {stats.trucks_preloaded === 1
                  ? `${city.name} truck`
                  : `${city.name} trucks`}{" "}
                loaded so far
              </h2>
              <p className="text-sm text-amber-950/75">
                Own one of these? Tap to claim your profile
                {promoActive ? " and get free Pro for the pilot." : "."}
              </p>
            </div>
            <ul className="space-y-2">
              {trucks.map((t) => (
                <li key={t.id}>
                  <Link
                    to={`/food-trucks/${citySlug}/${t.slug}`}
                    className="group block rounded-2xl bg-white/85 border border-amber-200 p-4 hover:border-[#ea580c] transition-colors"
                    data-testid={`preloaded-truck-${t.slug}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm sm:text-base truncate text-amber-950">
                          {t.truck_name}
                        </div>
                        <div className="text-xs text-amber-900/70 mt-0.5 inline-flex items-center gap-1.5 flex-wrap">
                          {t.cuisine_type && <span>{t.cuisine_type}</span>}
                          {t.food_truck_park && (
                            <>
                              <span className="text-amber-300">·</span>
                              <span className="inline-flex items-center gap-0.5">
                                <MapPin weight="duotone" className="h-3 w-3" />{" "}
                                {t.food_truck_park}
                              </span>
                            </>
                          )}
                          {t.claim_status === "verified" && (
                            <>
                              <span className="text-amber-300">·</span>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                                ✓ Claimed
                              </span>
                            </>
                          )}
                          {t.claim_status === "pending" && (
                            <>
                              <span className="text-amber-300">·</span>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
                                Claim pending
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <CaretRight
                        weight="bold"
                        className="h-4 w-4 text-amber-400 group-hover:text-[#ea580c] flex-none transition-colors"
                      />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <footer className="pt-4 border-t border-amber-200/60 text-xs text-amber-900/70 space-y-2">
          <div className="inline-flex items-center gap-1.5">
            <MapPin weight="duotone" className="h-3.5 w-3.5" />
            Built by a real food-truck owner in Lancaster, PA. Ember Food Truck.
          </div>
          <div>© {new Date().getFullYear()} TruckTrackr.</div>
        </footer>
      </main>
    </div>
  );
}
